import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import ChamaOnboarding from './ChamaOnboarding';
import ChamaDashboard from './ChamaDashboard';
import ChamaWallet from './ChamaWallet';
import ChamaTrading from './ChamaTrading';
import ChamaVotes from './ChamaVotes';
import ChamaLeaderboard from './ChamaLeaderboard';
import ChamaAnalytics from './ChamaAnalytics';
import ChamaChat from '../components/ChamaChat';
import ChamaDisputes from '../components/ChamaDisputes';

/**
 * ChamaMode — root router
 *
 * Usage in App.jsx / your routes:
 *   <ChamaMode />
 *
 * Reads chama_id from the JWT user object via /api/users/me
 * or falls back to localStorage.
 */
export default function ChamaMode() {
  const [chamaId, setChamaId] = useState(null);
  const [chamaName, setChamaName] = useState('');
  const [hasMembership, setHasMembership] = useState(null);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => { checkMembership(); }, []);

  const checkMembership = async () => {
    try {
      const res = await fetch('/api/chamas/my', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setChamaId(d.chama.id);
        setChamaName(d.chama.name);
        setHasMembership(true);
      } else {
        setHasMembership(false);
      }
    } catch {
      setHasMembership(false);
    }
  };

  const handleJoined = (chama) => {
    setChamaId(chama.id);
    setChamaName(chama.name);
    setHasMembership(true);
    navigate('/chama', { replace: true });
  };

  const onNavigate = (screen) => navigate(screen === 'dashboard' ? '/chama' : `/chama/${screen}`);
  const onBack = () => navigate('/chama');

  if (hasMembership === null) return <LoadingScreen />;
  if (hasMembership === false) return <ChamaOnboarding onJoined={handleJoined} />;

  return (
    <Routes>
      <Route index element={<ChamaDashboard chamaId={chamaId} onNavigate={onNavigate} />} />
      <Route path="wallet" element={<ChamaWallet chamaId={chamaId} onBack={onBack} />} />
      <Route path="trading" element={<ChamaTrading chamaId={chamaId} onBack={onBack} />} />
      <Route path="votes" element={<ChamaVotes chamaId={chamaId} onBack={onBack} />} />
      <Route path="chat" element={<ChamaChat chamaId={chamaId} onBack={onBack} />} />
      <Route path="disputes" element={<ChamaDisputes chamaId={chamaId} onBack={onBack} />} />
      <Route path="leaderboard" element={<ChamaLeaderboard chamaId={chamaId} chamaName={chamaName} onBack={onBack} />} />
      <Route path="analytics"   element={<ChamaAnalytics   chamaId={chamaId} onBack={onBack} />} />
      <Route path="*" element={<Navigate to="" replace />} />
    </Routes>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', background: '#08080a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        border: '2px solid rgba(55,114,255,0.2)',
        borderTop: '2px solid #3772ff',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: 'rgba(240,240,240,0.3)', fontSize: 13, fontFamily: "'Geist', sans-serif" }}>
        Loading Chama Mode
      </div>
    </div>
  );
}